"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";

interface BlogFilterPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  category: string;
  subcategory: string;
  heroImage: string;
  imageAlt: string;
  imageSrc: string;
}

interface BlogFilterProps {
  posts: BlogFilterPost[];
  categories: string[];
  subcategories: Record<string, string[]>;
}

function pillClass(active: boolean) {
  return `rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
    active
      ? "bg-coral text-ink-0 shadow-md shadow-coral/25"
      : "border border-white/12 bg-ink-1 text-paper/75 hover:border-coral hover:text-coral"
  }`;
}

function subPillClass(active: boolean) {
  return `rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
    active
      ? "bg-coral text-paper"
      : "border border-white/12 bg-ink-2 text-paper/70 hover:border-coral hover:text-coral"
  }`;
}

export function BlogFilter({ posts, categories, subcategories }: BlogFilterProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);

  const filteredPosts = posts.filter((post) => {
    if (selectedCategory && post.category !== selectedCategory) return false;
    if (selectedSubcategory && post.subcategory !== selectedSubcategory) return false;
    return true;
  });

  const activeSubcategories = selectedCategory ? (subcategories[selectedCategory] ?? []) : [];

  return (
    <>
      {/* Category nav */}
      <div className="rounded-2xl border border-white/12 bg-ink-1 p-5 lg:p-6">
        <p className="text-sm font-semibold text-paper">
          Browse by topic
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { setSelectedCategory(null); setSelectedSubcategory(null); }}
            className={pillClass(selectedCategory === null)}
          >
            All ({posts.length})
          </button>
          {categories.map((cat) => {
            const count = posts.filter((p) => p.category === cat).length;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => { setSelectedCategory(selectedCategory === cat ? null : cat); setSelectedSubcategory(null); }}
                className={pillClass(selectedCategory === cat)}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        {activeSubcategories.length > 0 && (
          <div className="mt-4 border-t border-white/12 pt-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper-dim">
              {selectedCategory} subcategories
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSelectedSubcategory(null)}
                className={subPillClass(selectedSubcategory === null)}
              >
                All
              </button>
              {activeSubcategories.map((sub) => {
                const count = posts.filter((p) => p.category === selectedCategory && p.subcategory === sub).length;
                return (
                  <button
                    key={sub}
                    type="button"
                    onClick={() => setSelectedSubcategory(selectedSubcategory === sub ? null : sub)}
                    className={subPillClass(selectedSubcategory === sub)}
                  >
                    {sub} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Result count */}
      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-paper/65">
        Showing <span className="font-semibold text-paper">{filteredPosts.length}</span>{" "}
        {filteredPosts.length === 1 ? "article" : "articles"}
      </p>

      {/* Posts grid */}
      {filteredPosts.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-white/12 bg-ink-1 p-10 text-center">
          <p className="text-paper/80">No articles match the selected filters.</p>
          <button
            type="button"
            onClick={() => { setSelectedCategory(null); setSelectedSubcategory(null); }}
            className="mt-4 rounded-full bg-coral px-6 py-2.5 text-sm font-semibold text-ink-0 shadow-md shadow-coral/25 transition hover:bg-ember"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map((post) => (
            <Link
              key={post.slug}
              href={`/articles/${post.slug}`}
              className="group flex flex-col surface-raised rounded-2xl"
            >
              <div className="relative aspect-[16/9] overflow-hidden rounded-t-2xl bg-ink-2">
                <Image
                  src={post.imageSrc}
                  alt={post.imageAlt || post.title}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <p className="text-xs font-semibold text-paper/75">
                  {post.category}
                  {post.subcategory && (
                    <span className="text-paper/50"> · {post.subcategory}</span>
                  )}
                </p>
                <h2 className="mt-3 font-display text-lg font-medium leading-snug tracking-[-0.01em] text-paper group-hover:text-coral">
                  {post.title}
                </h2>
                <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-paper/75">
                  {post.description}
                </p>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-paper-dim">
                  {new Intl.DateTimeFormat("en-NZ", { day: "numeric", month: "long", year: "numeric" }).format(new Date(post.date))}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
